<template>
    <div
        v-if="readonly"
        class="status-label read-only"
        :class="[
            { 'form-control': isForm },
            { 'no-stock': noStock },
            { success: value === 'publish' },
            readonlyValueClass,
        ]"
        style="display: flex; align-items: center"
    >
        <!-- <i class="icon-Status status-dot-indictator"></i> -->
        {{ getOption(value) }}
        <!-- optional trailing detail, e.g. an approval progress count next to the status -->
        <span
            v-if="suffix"
            class="select-suffix"
            >{{ suffix }}</span
        >
        <!-- for local search sort-->
        <span style="display: none">{{ getOption(value) }}</span>
    </div>
    <div
        v-else-if="visibleItems != null"
        class="bo-select bo-select-styled"
        :class="[customClass, { 'is-open': isOpen, 'is-disabled': disabled }]"
        ref="wrapper"
        v-click-outside="closeDropdown"
    >
        <!-- Custom styled dropdown (when visibleItems is set) -->
        <div
            class="bo-select__trigger"
            @click="toggleDropdown"
        >
            <span class="bo-select__value">{{ labels[selectedItem] }}</span>
            <span class="bo-select__arrow">
                <svg
                    width="10"
                    height="6"
                    viewBox="0 0 10 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M1 1L5 5L9 1"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </span>
        </div>

        <transition name="bo-select-fade">
            <div
                v-if="isOpen"
                class="bo-select__dropdown"
                :style="dropdownStyle"
            >
                <div
                    v-if="nullable && !multiple"
                    class="bo-select__item"
                    :class="{ 'is-selected': selectedItem === null }"
                    @click="selectOption(null)"
                >
                    {{ nullText }}
                </div>

                <div
                    v-for="(value, key) in options"
                    :key="value"
                    class="bo-select__item"
                    :class="{ 'is-selected': isSelected(value) }"
                    @click="selectOption(value)"
                >
                    {{ labels[value] }}
                </div>
            </div>
        </transition>
    </div>
    <div
        v-else
        class="bo-select"
    >
        <!-- <select
            v-if="!disabled && !plain"
            v-model="selectedItem"
            :multiple="multiple"
            :disabled="disabled"
            :class="[{ 'form-control': isForm }, { 'no-stock': noStock }, inputStyle]"
        > -->
        <select
            v-model="selectedItem"
            :multiple="multiple"
            :disabled="disabled"
            :id="id"
            :class="[{ 'form-control': isForm }, { 'no-stock': noStock }, inputStyle]"
            :style="selectedItem === null ? 'font-weight: 400; color: #6c757d;' : ''"
        >
            <option
                v-if="nullable && !multiple"
                :value="null"
                style="font-weight: 400; color: #6c757d;"
            >
                {{ nullText }}
            </option>
            <option
                v-if="placeholder && placeholder != '' && !multiple && selectedItem === ''"
                value=""
                selected
                disabled
                hidden
            >
                {{ placeholder }}
            </option>
            <template v-for="option in options">
                <slot
                    name="option"
                    :option="option"
                >
                    <option
                        :key="option"
                        :value="option"
                    >
                        <slot
                            name="option_text"
                            :option="option"
                        >
                            {{ getOption(option) }}
                            {{ getDescription(option) === null ? "" : `(${getDescription(option)})` }}
                        </slot>
                    </option>
                </slot>
            </template>
        </select>
        <!-- <span v-else>{{ getOption(value) }}</span> -->
    </div>
</template>

<script>
export default {
    directives: {
        clickOutside: {
            bind(el, binding) {
                el._clickOutsideHandler = (event) => {
                    if (!el.contains(event.target)) binding.value(event);
                };
                document.addEventListener("click", el._clickOutsideHandler, true);
            },
            unbind(el) {
                document.removeEventListener("click", el._clickOutsideHandler, true);
            },
        },
    },

    props: {
        id: {
            type: String,
            default: "",
        },
        // readonly cells only - rendered after the resolved label
        suffix: {
            type: [String, Number],
            default: null,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [String, Number, Boolean],
        },
        options: {
            type: [Array, Object],
            default: () => {
                return {};
            },
        },
        labels: {
            type: [Array, Object],
            default: () => {
                return {};
            },
        },
        placeholder: {
            type: String,
            default: "",
        },
        nullable: {
            type: Boolean,
            default: false,
        },
        nullText: {
            type: String,
            default: () => window.bo?.generalTranslations?.select ?? "Select",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        // Display text as is, without the select border
        plain: {
            type: Boolean,
            default: false,
        },
        // Make styling as form-control
        isForm: {
            type: Boolean,
            default: false,
        },
        noStock: {
            type: Boolean,
            default: false,
        },
        inputStyle: {
            type: String,
            default: "",
        },
        descriptions: {
            type: [Array, Object],
            default: () => {
                return {};
            },
        },
        onChangeConfirm: {
            type: Object,
            default: null,
        },
        customClass: {
            type: String,
            default: "",
        },
        visibleItems: {
            type: Number,
            default: null,
        },
    },

    model: {
        prop: "value",
        event: "change",
    },

    computed: {
        readonlyValueClass() {
            if (typeof this.value !== "string" && typeof this.value !== "number") {
                return "";
            }

            return `status-${String(this.value)
                .toLowerCase()
                .replace(/[^a-z0-9-]+/g, "-")}`;
        },

        selectedItem: {
            get() {
                return this.value ?? null;
            },
            set(newValue) {
                if (!this.disabled) {
                    if (this.onChangeConfirm && this.$root.$refs.bo_confirmation) {
                        const confirmTitle =
                            this.onChangeConfirm.title || (window.bo?.generalTranslations?.confirm ?? "Confirm");
                        const confirmMessage =
                            this.onChangeConfirm.message ||
                            (window.bo?.generalTranslations?.action_object_question
                                ?.replace(":action", "change")
                                .replace(":object", "value") ??
                                "Are you sure you want to change this value?");
                        const confirmButtonText =
                            this.onChangeConfirm.confirmButtonText ||
                            (window.bo?.generalTranslations?.confirm ?? "Confirm");
                        const confirmButtonColour = this.onChangeConfirm.confirmButtonColour || "info";
                        const cancelButton =
                            this.onChangeConfirm.cancelButton !== undefined ? this.onChangeConfirm.cancelButton : true;

                        this.$root.$refs.bo_confirmation
                            .activate(
                                confirmTitle,
                                confirmMessage,
                                confirmButtonText,
                                confirmButtonColour,
                                cancelButton
                            )
                            .then((confirm) => {
                                if (confirm) {
                                    this.$emit("change", newValue);
                                } else {
                                    this.$forceUpdate();
                                }
                            });
                    } else {
                        this.$emit("change", newValue);
                    }
                }
            },
        },
        isFormControl() {
            return this.customClass && this.customClass.split(" ").includes("form-control");
        },
        dropdownStyle() {
            const itemH = this.isFormControl ? 34 : 44;
            return { maxHeight: this.visibleItems * itemH + "px" };
        },
    },

    data() {
        return {
            isOpen: false,
        };
    },

    methods: {
        getOption(option) {
            if (this.labels && this.labels[option]) {
                return this.labels[option];
            } else if (option) {
                if (option.label) {
                    return option.label;
                }
                return option;
            }
            return "";
        },
        getDescription(option) {
            if (this.descriptions && this.descriptions[option]) {
                return this.descriptions[option];
            } else if (option && option.description) {
                return option.description;
            }
            return null;
        },
        toggleDropdown() {
            if (this.disabled) return;
            this.isOpen = !this.isOpen;
        },
        closeDropdown() {
            this.isOpen = false;
        },
        selectOption(key) {
            this.selectedItem = key;
            if (!this.multiple) this.isOpen = false;
        },
        isSelected(key) {
            if (this.multiple) {
                return Array.isArray(this.selectedItem) && this.selectedItem.includes(key);
            }
            return this.selectedItem == key;
        },
    },
};
</script>

<style>
.bo-select {
    width: 100%;
}
</style>
